import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BillingToggle from '../components/pricing/BillingToggle';
import PricingCard   from '../components/pricing/PricingCard';
import { apiFetch }  from '../lib/api';
import { useAuth }   from '../context/AuthContext';
import PageSEO       from '../components/PageSEO';

// Shape returned by GET /api/stripe/prices
interface PriceRow {
  tier:                 number;
  name:                 string;
  monthlyUsd:           number;
  annualUsd:            number;
  contactLimit:         number | null;
  includedCardsPerYear: number;
  overageCents:         number;
  priceMonthly:         string;
  priceAnnual:          string;
}

// ─── FAQ data ────────────────────────────────────────────────────────────────

const FAQ = [
  {
    q: 'Are these real physical cards or digital cards?',
    a: 'Real, physical folded greeting cards, printed on quality cardstock and mailed first-class in a sealed envelope through the postal service. MomentSent is not an eCard or a digital "experience page." A tangible card arrives in your recipient\'s mailbox.',
  },
  {
    q: 'How does pricing work?',
    a: 'Each plan includes a set number of folded cards every year: 12 on Thoughtful, 24 on Connected, and 48 on Premium, with printing and first-class postage included. If you send more than your yearly allowance, extra cards are a flat $5.99 each, billed only when you go past the included amount. Your plan price also sets how many people you can send to (up to 5, up to 20, or unlimited).',
  },
  {
    q: 'What happens if I go over my included cards?',
    a: 'Nothing stops or breaks. Any card beyond your yearly allowance is simply $5.99, added to your next invoice. Most people never reach their allowance in a year, and the included cards bank up over the year so a busy month is covered.',
  },
  {
    q: 'Is it automatic every year?',
    a: 'Yes. Set a recurring date like a birthday once and MomentSent mails a real folded card automatically every year, with no re-setup.',
  },
  {
    q: 'Do I need a credit card to start?',
    a: 'No. Your 14-day trial starts the moment you sign up, with no payment info required. We only ask for a card if you decide to continue after the trial.',
  },
  {
    q: 'What happens when my trial ends?',
    a: 'If you haven\'t added a payment method, your subscription is automatically cancelled and no charge is made. You can always come back and subscribe later.',
  },
  {
    q: 'Can I switch plans anytime?',
    a: 'Yes. Upgrade or downgrade at any time from your account settings. Upgrades take effect immediately; downgrades apply at the next billing cycle.',
  },
  {
    q: 'Who actually sends the cards?',
    a: 'We partner with a professional card printing and mailing service. You never touch a card. We handle everything from printing the folded card to stuffing the envelope and first-class postage.',
  },
];

// ─── Structured data (JSON-LD) ───────────────────────────────────────────────
// Product + AggregateOffer pin the real pricing so AI search engines quote
// $7.99–$27.99, each plan including a yearly allowance of real folded cards
// (12 / 24 / 48) with extra cards at a flat $5.99, not a guess. FAQPage mirrors
// the visible FAQ below.

const PRICING_STRUCTURED_DATA = [
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'MomentSent',
    description:
      'Automatic greeting card mailing service. Set a recurring date once and MomentSent mails a real, folded greeting card in a sealed envelope every year. Plans set how many people you send to (up to 5, up to 20, or unlimited) and include a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included. Extra cards beyond the allowance are a flat $5.99 each.',
    brand: { '@type': 'Brand', name: 'MomentSent' },
    category: 'Greeting card mailing service',
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '7.99',
      highPrice: '27.99',
      offerCount: 3,
      offers: [
        {
          '@type': 'Offer',
          name: 'Thoughtful',
          price: '7.99',
          priceCurrency: 'USD',
          description:
            "Up to 5 people, with cards for Birthday, Valentine's Day, Mother's Day and Father's Day. Includes 12 folded cards a year with printing and first-class postage; extra cards are $5.99 each. 14-day free trial, no credit card required.",
        },
        {
          '@type': 'Offer',
          name: 'Connected',
          price: '15.99',
          priceCurrency: 'USD',
          description: 'Up to 20 people, all occasion types. Includes 24 folded cards a year; extra cards are $5.99 each.',
        },
        {
          '@type': 'Offer',
          name: 'Premium',
          price: '27.99',
          priceCurrency: 'USD',
          description: 'Unlimited people, every occasion. Includes 48 folded cards a year; extra cards are $5.99 each.',
        },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  },
];

// ─── PricingPage ──────────────────────────────────────────────────────────────

export default function PricingPage() {
  const { user } = useAuth();
  const [searchParams]   = useSearchParams();
  const reason           = searchParams.get('reason');
  const [annual,       setAnnual  ] = useState(false);
  const [prices,       setPrices  ] = useState<PriceRow[]>([]);
  const [loading,      setLoading ] = useState(true);
  const [priceError,   setPriceError] = useState(false);
  const [faqOpen,      setFaqOpen ] = useState<number | null>(null);

  function loadPrices() {
    setLoading(true);
    setPriceError(false);
    apiFetch<{ prices: PriceRow[] }>('/api/stripe/prices')
      .then((json) => setPrices(json.prices ?? []))
      .catch(() => setPriceError(true))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadPrices(); }, []);

  return (
    <div className="min-h-screen bg-cream">
      <PageSEO
        title="Pricing | MomentSent Plans Starting at $7.99/month"
        description="Simple, honest pricing with a free 14-day trial and no credit card required. Plans from $7.99 a month for 5 contacts up to $27.99 a month for unlimited contacts. Each plan includes a yearly allowance of real folded cards (12, 24, or 48); extra cards are a flat $5.99."
        canonical="https://momentsent.com/pricing"
        structuredData={PRICING_STRUCTURED_DATA}
      />

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav className="flex items-center justify-between px-6 py-5 max-w-6xl mx-auto">
        <Link to="/" className="font-heading text-xl font-semibold text-navy">
          MomentSent
        </Link>
        {/* Center nav links — desktop only */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/#how-it-works" className="font-body text-sm text-gray-500 hover:text-navy transition-colors">
            How it works
          </Link>
          <Link to="/#features" className="font-body text-sm text-gray-500 hover:text-navy transition-colors">
            Features
          </Link>
          <Link to="/about" className="font-body text-sm text-gray-500 hover:text-navy transition-colors">
            About
          </Link>
          <Link to="/contact" className="font-body text-sm text-gray-500 hover:text-navy transition-colors">
            Contact
          </Link>
        </div>
        <div className="flex items-center gap-4">
          {user ? (
            <>
              <Link to="/dashboard" className="font-body text-sm text-gray-500 hover:text-navy transition-colors">
                Dashboard
              </Link>
              <Link to="/account" className="btn-primary text-sm px-4 py-2">
                Account
              </Link>
            </>
          ) : (
            <>
              <Link to="/login"  className="font-body text-sm text-gray-500 hover:text-navy transition-colors">
                Sign in
              </Link>
              <Link to="/signup" className="btn-primary text-sm px-4 py-2">
                Get started
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* ── Upgrade reason banner ───────────────────────────────────────── */}
      {reason === 'contact_limit' && (
        <div className="mx-auto max-w-2xl px-6 pt-6">
          <div className="flex items-start gap-3 rounded-[16px] bg-navy/5 border border-navy/15 px-5 py-4">
            <span className="text-xl flex-shrink-0 mt-0.5">🔒</span>
            <div>
              <p className="font-heading text-sm font-semibold text-navy">You've reached your contact limit</p>
              <p className="font-body text-sm text-gray-600 mt-0.5">
                Your current plan is full. Upgrade below to add more people and keep those relationships going.
              </p>
            </div>
          </div>
        </div>
      )}
      {reason === 'occasion_type_limit' && (
        <div className="mx-auto max-w-2xl px-6 pt-6">
          <div className="flex items-start gap-3 rounded-[16px] bg-navy/5 border border-navy/15 px-5 py-4">
            <span className="text-xl flex-shrink-0 mt-0.5">🔒</span>
            <div>
              <p className="font-heading text-sm font-semibold text-navy">This occasion type requires an upgrade</p>
              <p className="font-body text-sm text-gray-600 mt-0.5">
                The Thoughtful plan includes Birthday, Mother's Day, Father's Day, and Valentine's Day.
                Upgrade to the Connected or Premium plan to unlock all occasion types.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="text-center px-6 pt-12 pb-8 max-w-2xl mx-auto">
        <p className="font-accent italic text-sage text-base mb-3">
          Simple, honest pricing
        </p>
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-navy leading-tight mb-4">
          Never miss a moment
          <br />
          <span className="text-sage">that matters.</span>
        </h1>
        <p className="font-body text-gray-500 text-base leading-relaxed max-w-lg mx-auto mb-8">
          Every plan includes a yearly allowance of real folded cards, with printing
          and first-class postage included. Send past it and extra cards are a flat
          $5.99. Start with a 14-day free trial, no credit card required.
        </p>

        {/* Billing toggle */}
        <BillingToggle annual={annual} onChange={setAnnual} />
      </section>

      {/* ── Pricing cards ────────────────────────────────────────────────── */}
      <section className="px-6 pb-16 max-w-5xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="h-10 w-10 rounded-full border-2 border-sage border-t-transparent animate-spin" />
          </div>
        ) : priceError ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <p className="font-body text-gray-500 text-sm">Could not load pricing. Please try again.</p>
            <button
              onClick={loadPrices}
              className="btn-primary text-sm px-6 py-2"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {prices.map((p) => (
              <PricingCard
                key={p.tier}
                {...p}
                annual={annual}
                popular={p.tier === 2}
              />
            ))}
          </div>
        )}

        {/* Trust strip */}
        <ul className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-body text-gray-500">
          {[
            '✓  Cancel anytime',
            '✓  No credit card for trial',
            '✓  Printing & postage included',
            '✓  Cards mailed first-class',
            '✓  Secure payments via Stripe',
          ].map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      {/* ── Comparison table (desktop) ───────────────────────────────────── */}
      <section className="hidden md:block px-6 pb-20 max-w-4xl mx-auto">
        <h2 className="font-heading text-2xl font-semibold text-navy text-center mb-8">
          Compare plans
        </h2>

        <div className="overflow-x-auto rounded-[20px] shadow-warm bg-white">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-6 py-4 text-gray-400 font-medium w-1/3">Feature</th>
                <th className="px-6 py-4 text-center text-navy font-semibold">Thoughtful</th>
                <th className="px-6 py-4 text-center text-navy font-semibold bg-navy/[0.03]">
                  Connected
                  <span className="ml-1.5 rounded-full bg-sage/20 px-2 py-0.5 text-xs text-sage font-semibold">
                    Popular
                  </span>
                </th>
                <th className="px-6 py-4 text-center text-navy font-semibold">Premium</th>
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.map((row, i) => (
                <tr key={row.label} className={i % 2 === 0 ? '' : 'bg-gray-50/60'}>
                  <td className="px-6 py-3.5 text-gray-600">{row.label}</td>
                  <TableCell value={row.t1} />
                  <TableCell value={row.t2} highlight />
                  <TableCell value={row.t3} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section className="px-6 pb-24 max-w-2xl mx-auto">
        <h2 className="font-heading text-2xl font-semibold text-navy text-center mb-8">
          Frequently asked questions
        </h2>

        <div className="flex flex-col gap-3">
          {FAQ.map((item, i) => (
            <div
              key={item.q}
              className="rounded-[16px] bg-white shadow-warm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-4 text-left font-body font-medium text-navy hover:bg-gray-50/50 transition-colors"
              >
                <span>{item.q}</span>
                <svg
                  className={`h-4 w-4 flex-shrink-0 text-sage transition-transform ${
                    faqOpen === i ? 'rotate-45' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </button>

              {faqOpen === i && (
                <p className="px-6 pb-5 text-sm text-gray-500 leading-relaxed font-body">
                  {item.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer CTA ───────────────────────────────────────────────────── */}
      <section className="bg-navy text-white py-16 px-6 text-center">
        <p className="font-accent italic text-sage text-base mb-3">Ready to start?</p>
        <h2 className="font-heading text-3xl font-bold mb-4">
          Try MomentSent free for 14 days.
        </h2>
        <p className="font-body text-white/70 mb-8 max-w-md mx-auto text-sm leading-relaxed">
          No credit card, no commitment. Just the people you care about,
          getting cards that feel like you wrote them.
        </p>
        <Link
          to="/signup"
          className="inline-block bg-sage text-navy font-body font-semibold text-sm px-8 py-3.5 rounded-xl hover:bg-sage/90 transition-colors"
        >
          Start your free trial
        </Link>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="bg-navy border-t border-white/10 text-white/50 py-6 px-6 text-center">
        <p className="font-body text-xs">
          © {new Date().getFullYear()} MomentSent, LLC · Registered in North Carolina ·{' '}
          <Link to="/privacy" className="hover:text-white/80 transition-colors">Privacy</Link>
          {' · '}
          <Link to="/terms"   className="hover:text-white/80 transition-colors">Terms</Link>
        </p>
      </footer>
    </div>
  );
}

// ─── Comparison table data ────────────────────────────────────────────────────

type CellValue = string | boolean;

const TABLE_ROWS: { label: string; t1: CellValue; t2: CellValue; t3: CellValue }[] = [
  { label: 'Folded cards included / year', t1: '12',  t2: '24',             t3: '48'        },
  { label: 'Extra cards beyond allowance', t1: '$5.99 ea', t2: '$5.99 ea',  t3: '$5.99 ea'  },
  { label: 'Printing + first-class postage', t1: true, t2: true,            t3: true        },
  { label: 'People',                t1: 'Up to 5',    t2: 'Up to 20',       t3: 'Unlimited' },
  { label: 'Personally written msg',t1: true,          t2: true,              t3: true        },
  { label: 'Occasion types',        t1: '4',           t2: 'All',             t3: 'All'       },
  { label: 'Custom occasions',      t1: false,         t2: 'Up to 3',         t3: 'Unlimited' },
  { label: 'Card preview window',   t1: '14 days',     t2: '14 days',         t3: '30 days'   },
  { label: 'Relationship Wrapped, yearly recap', t1: false, t2: false,       t3: true        },
  { label: 'Send a card now, no occasion needed', t1: false, t2: false,     t3: true        },
  { label: 'Free trial',            t1: '14 days',     t2: '14 days',         t3: '14 days'   },
];

function TableCell({ value, highlight }: { value: CellValue; highlight?: boolean }) {
  const bg = highlight ? 'bg-navy/[0.03]' : '';

  if (typeof value === 'boolean') {
    return (
      <td className={`px-6 py-3.5 text-center ${bg}`}>
        {value ? (
          <svg className="h-4 w-4 text-sage mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <span className="text-gray-300 text-base leading-none">–</span>
        )}
      </td>
    );
  }

  return (
    <td className={`px-6 py-3.5 text-center text-gray-700 font-medium ${bg}`}>
      {value}
    </td>
  );
}
